import React, { useRef } from 'react';
import useContainerWidth from '../../../hooks/useContainerWidth';

const SUBTLE = '#8A8A8A';
const INK = '#000';

/** "2026-09-26" → a local Date (new Date(iso) would read it as UTC midnight and print the day before). */
export const localDay = (iso) => {
  if (!iso) return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(iso));
  return m ? new Date(+m[1], +m[2] - 1, +m[3]) : new Date(iso);
};

const MONTH = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'];
const WEEKDAY = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'];

/**
 * The words under one column of a day chart. A week reads "MON 21"; a longer run reads the
 * day number, with the month on the first column and on every 1st so the run stays dated.
 */
export const dayLabel = (iso, i, n) => {
  const d = localDay(iso);
  if (!d) return '';
  if (n <= 7) return `${WEEKDAY[d.getDay()]} ${d.getDate()}`;
  if (i === 0 || d.getDate() === 1) return `${MONTH[d.getMonth()]} ${d.getDate()}`;
  return String(d.getDate());
};

/**
 * Which columns carry a label: as many as fit at `labelWidth` px each, counted from the last
 * column so the most recent day is always named. Everything else gets a tick.
 */
export const labelledIndexes = (n, width, labelWidth) => {
  if (!n || !width) return new Set();
  const col = width / n;
  const step = Math.max(1, Math.ceil(labelWidth / col));
  const out = new Set();
  for (let i = n - 1; i >= 0; i -= step) out.add(i);
  return out;
};

/**
 * One label cell per column, laid out exactly like the bars above it (same flex + gap), so each
 * day sits under its own bar. Every day gets a tick; a label where there is room for one.
 */
export default function DayAxis({ days, gap = 6, labelWidth = 34, size = 9, testId = 'day-axis' }) {
  const ref = useRef(null);
  const width = useContainerWidth(ref);
  const n = days.length;
  const shown = labelledIndexes(n, width, labelWidth + gap);
  if (!n) return null;
  return (
    <div ref={ref} className="flex" style={{ gap }} data-testid={testId} aria-hidden="true">
      {days.map((day, i) => {
        const on = shown.has(i);
        return (
          <span key={day} className="flex min-w-0 flex-1 flex-col items-center" style={{ gap: 4 }}>
            <span style={{ width: 1, height: on ? 6 : 3, background: on ? INK : '#D4D4D4' }} />
            <span className="whitespace-nowrap" style={{ fontSize: size, fontWeight: 700, letterSpacing: '.08em', color: on ? INK : SUBTLE, lineHeight: 1, visibility: on ? 'visible' : 'hidden' }}>{dayLabel(day, i, n)}</span>
          </span>
        );
      })}
    </div>
  );
}
